import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { AWNING_FROM_PRICE } from '../config/startingPrice';

const SITE_NAME = 'ASG Shading';
const SITE_URL = 'https://asgshading.com.au';
const DEFAULT_TITLE = `${SITE_NAME} - Retractable Awnings & Pergolas Melbourne`;
const DEFAULT_DESCRIPTION = 'Made-to-measure retractable awnings with free delivery to most of Melbourne, plus supply-only aluminium pergola systems. Motorised cassette and open-arm models.';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

// Google shows roughly 60 characters of a title and 160 of a description and
// cuts the rest mid-word. Every title tries to carry the brand; when that
// would push it over the limit the brand is dropped rather than letting
// Google chop the end off. Descriptions are clamped at a word boundary as a
// safety net for copy that comes from the database (products, blog posts).
// Hand-written descriptions should still be written to fit: 70 to 160.
const MAX_TITLE_LENGTH = 60;
const MAX_DESCRIPTION_LENGTH = 160;

export function pageTitle(title) {
  if (!title) return DEFAULT_TITLE;
  const branded = `${title} | ${SITE_NAME}`;
  return branded.length <= MAX_TITLE_LENGTH ? branded : title;
}

export function clampDescription(text, max = MAX_DESCRIPTION_LENGTH) {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max - 3);
  const atWord = cut.slice(0, cut.lastIndexOf(' ')).replace(/[\s,;:-]+$/, '');
  return `${atWord || cut}...`;
}

// Open Graph and Twitter require absolute URLs - a relative path silently
// breaks the preview card on Facebook, LinkedIn and WhatsApp rather than
// erroring. Callers pass whatever the API gave them (usually a root-relative
// path), so every image URL is normalised here rather than at each call site,
// where one missed caller would reintroduce the bug.
export function absoluteUrl(url) {
  if (!url || typeof url !== 'string') return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('//')) return `https:${url}`;
  return `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

// The pre-rendered <head> already carries this page's tags. Hydration adopts
// them when they match what React renders; when they do not (a filtered view
// such as /blog?tag=x is served /blog's HTML), React adds its own and the
// served ones linger, so a crawler running JavaScript sees two descriptions
// and two canonicals. After each render, keep only the newest tag per key.
const HEAD_KEYS = [
  ['meta[name="description"]', () => 'description'],
  ['meta[name="robots"]', () => 'robots'],
  ['link[rel="canonical"]', () => 'canonical'],
  ['meta[property^="og:"]', (el) => `og:${el.getAttribute('property')}`],
  ['meta[name^="twitter:"]', (el) => `tw:${el.getAttribute('name')}`],
  ['meta[name^="geo."], meta[name="ICBM"], meta[name="article:modified_time"]', (el) => `n:${el.getAttribute('name')}`],
  ['script[type="application/ld+json"]', () => 'ld+json'],
];

function dropStaleHeadTags() {
  if (typeof document === 'undefined') return;
  for (const [selector, keyOf] of HEAD_KEYS) {
    const seen = new Map();
    for (const el of document.head.querySelectorAll(selector)) seen.set(keyOf(el), el);
    for (const el of document.head.querySelectorAll(selector)) {
      if (seen.get(keyOf(el)) !== el) el.remove();
    }
  }
}

function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  noindex = false,
  jsonLd,
  dateModified,
  children,
}) {
  const fullTitle = pageTitle(title);
  const metaDescription = clampDescription(description) || DEFAULT_DESCRIPTION;
  const cleanCanonical = canonical ? canonical.replace(/\/+$/, '') || '/' : null;
  const canonicalUrl = cleanCanonical ? `${SITE_URL}${cleanCanonical}` : undefined;
  const image = absoluteUrl(ogImage) || DEFAULT_IMAGE;

  useEffect(() => { dropStaleHeadTags(); }, [fullTitle, metaDescription, canonicalUrl, image]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {/* keywords meta tag intentionally omitted - ignored by Google since 2009 */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content="en_AU" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />

      {/* Freshness */}
      {dateModified && <meta name="article:modified_time" content={dateModified} />}
      {dateModified && <meta property="og:updated_time" content={dateModified} />}

      {/* Geo */}
      <meta name="geo.region" content="AU-VIC" />
      <meta name="geo.placename" content="Hoppers Crossing, Melbourne" />
      <meta name="geo.position" content="-37.8848;144.6991" />
      <meta name="ICBM" content="-37.8848, 144.6991" />

      {/* JSON-LD structured data - consolidate arrays into @graph */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(jsonLd)
              ? {
                  '@context': 'https://schema.org',
                  '@graph': jsonLd.map(({ '@context': _ctx, ...rest }) => rest),
                }
              : jsonLd
          )}
        </script>
      )}

      {children}
    </Helmet>
  );
}

// Pre-built JSON-LD schemas
SEO.organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/logo512.png`,
    width: 512,
    height: 512,
  },
  description: 'ASG Shading supplies premium made-to-measure retractable awnings across Melbourne and custom aluminium pergola systems - louvered, retractable and flat-blade.',
  foundingDate: '2014',
  knowsAbout: [
    'retractable awning supply',
    'motorised cassette awnings',
    'pergola supply',
    'louvered roof systems',
    'outdoor living design',
    'shade solutions',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+61422439499',
    email: 'info@asgshading.com.au',
    contactType: 'sales',
    areaServed: 'AU',
    availableLanguage: 'English',
  },
};

SEO.localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'HomeAndConstructionBusiness',
  '@id': `${SITE_URL}/#localbusiness`,
  name: SITE_NAME,
  url: SITE_URL,
  image: `${SITE_URL}/og-image.jpg`,
  telephone: '+61422439499',
  email: 'info@asgshading.com.au',
  logo: `${SITE_URL}/logo512.png`,
  // ABN published on the Privacy and Terms pages; kept identical to those.
  identifier: {
    '@type': 'PropertyValue',
    name: 'ABN',
    value: '54 658 871 369',
  },
  // Street address deliberately omitted - this is a warehouse, not a
  // showroom, and we do not take walk-ins.
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Hoppers Crossing',
    addressRegion: 'VIC',
    postalCode: '3029',
    addressCountry: 'AU',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: -37.8848,
    longitude: 144.6991,
  },
  areaServed: [
    { '@type': 'City', name: 'Melbourne', '@id': 'https://www.wikidata.org/wiki/Q3141' },
    { '@type': 'City', name: 'Hoppers Crossing' },
    { '@type': 'City', name: 'Werribee' },
    { '@type': 'City', name: 'Point Cook' },
    { '@type': 'City', name: 'Williamstown' },
    { '@type': 'City', name: 'Footscray' },
    { '@type': 'City', name: 'Sunshine' },
    { '@type': 'City', name: 'Geelong' },
    { '@type': 'City', name: 'Dandenong' },
    { '@type': 'City', name: 'Pakenham' },
    { '@type': 'City', name: 'Berwick' },
    { '@type': 'City', name: 'Frankston' },
    { '@type': 'City', name: 'Cranbourne' },
    { '@type': 'City', name: 'Epping' },
    {
      '@type': 'GeoCircle',
      geoMidpoint: { '@type': 'GeoCoordinates', latitude: -37.8848, longitude: 144.6991 },
      geoRadius: '80000',
    },
  ],
  hasMap: 'https://maps.google.com/?q=ASG+Architectural+Shading+Hoppers+Crossing+VIC',
  // Was '$5,000-$35,000 AUD', which contradicted the advertised awning
  // from-price. Driven from the same constant the site displays.
  priceRange: `$${AWNING_FROM_PRICE} - $35,000 AUD`,
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '08:00', closes: '18:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '09:00', closes: '16:00' },
  ],
};

SEO.breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [{ name: 'Home', url: '/' }, ...items].map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: item.url ? `${SITE_URL}${item.url}` : undefined,
  })),
});

// Product structured data.
//
// aggregateRating and review are deliberately ABSENT. We have no reviews, and
// inventing them would be a fabricated rating in Google's rich results - a
// breach of Google's review snippet policy and of ACL s29 (false testimonials).
// Add these fields ONLY once genuine, verifiable customer reviews exist, and
// then only with real counts and scores drawn from those reviews.
SEO.productSchema = (product) => {
  const productUrl = `${SITE_URL}/products/${product.slug || product._id}`;
  // Google warns when priceValidUntil is in the past, so it rolls forward a
  // year from build rather than sitting at a hardcoded date that silently
  // expires.
  const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
    .toISOString().slice(0, 10);
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.images && product.images.length > 0
      ? product.images.map(absoluteUrl)
      : undefined,
    url: productUrl,
    brand: { '@type': 'Brand', name: SITE_NAME },
    // No product carries an explicit sku, and the Mongo _id is an opaque
    // ObjectId that would surface in search results as the SKU and change if
    // the data were ever reseeded. The slug is stable, unique and readable.
    sku: product.sku || (product.slug ? product.slug.toUpperCase() : product._id),
    ...(product.price_from && {
      offers: {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'AUD',
        price: product.price_from,
        priceValidUntil,
        availability: 'https://schema.org/InStock',
      },
    }),
  };
};

SEO.articleSchema = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.seo_description || post.excerpt,
  image: post.featured_image
    ? (post.featured_image.startsWith('http') ? post.featured_image : `${SITE_URL}${post.featured_image}`)
    : `${SITE_URL}/og-image.jpg`,
  datePublished: post.createdAt,
  dateModified: post.updatedAt || post.createdAt,
  // CQ-18: prefer snake_case author field, fall back to deprecated
  // camelCase until the migration script renames the data.
  author: (post.author_name || post.authorName)
    ? { '@type': 'Person', name: post.author_name || post.authorName }
    : { '@type': 'Organization', name: SITE_NAME },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo512.png` },
  },
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` },
  ...(post.content && { wordCount: post.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length }),
});

SEO.webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
};

SEO.serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Retractable Awnings Melbourne',
  provider: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  areaServed: [
    { '@type': 'City', name: 'Melbourne' },
    { '@type': 'City', name: 'Hoppers Crossing' },
    { '@type': 'City', name: 'Werribee' },
    { '@type': 'City', name: 'Geelong' },
    { '@type': 'City', name: 'Dandenong' },
    { '@type': 'City', name: 'Pakenham' },
    { '@type': 'City', name: 'Frankston' },
    { '@type': 'City', name: 'Epping' },
  ],
  description: 'Premium made-to-measure motorised retractable awnings (cassette and open-arm), delivered across the Melbourne metropolitan area, based in Hoppers Crossing.',
  serviceType: 'Retractable Awning Supply',
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'AUD',
    lowPrice: '1000',
    highPrice: '6000',
    availability: 'https://schema.org/InStock',
  },
};

SEO.faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
});

// Currently unused, and must stay that way until real reviews exist. Wiring
// this up with invented ratings would be a fabricated testimonial - see the
// note on productSchema above.
SEO.reviewSchema = (review) => ({
  '@context': 'https://schema.org',
  '@type': 'Review',
  reviewBody: review.text,
  ...(review.datePublished && { datePublished: review.datePublished }),
  author: { '@type': 'Person', name: review.author },
  itemReviewed: {
    '@type': 'HomeAndConstructionBusiness',
    name: SITE_NAME,
    '@id': `${SITE_URL}/#localbusiness`,
  },
  reviewRating: {
    '@type': 'Rating',
    ratingValue: review.rating || 5,
    bestRating: 5,
  },
});

SEO.webPageSchema = (page) => ({
  '@context': 'https://schema.org',
  '@type': page.type || 'WebPage',
  name: page.name,
  description: page.description,
  url: `${SITE_URL}${page.url || ''}`,
  isPartOf: { '@type': 'WebSite', '@id': `${SITE_URL}/#website` },
  ...(page.dateModified && { dateModified: page.dateModified }),
});

SEO.itemListSchema = (products) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: products.map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: p.name,
    url: `${SITE_URL}/products/${p.slug || p._id}`,
  })),
});

export default SEO;
